/**
 * LandingHeader — premium dark-on-hero, frosted white when scrolled.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LayoutDashboard } from 'lucide-react';
import { useAuth } from '../../store/auth';
import { homePathForUser } from '../../utils/workspace';
import AgeonLogo from '../../components/AgeonLogo';
import ProfileDropdown from '../../components/ProfileDropdown';

const NAV = [
    { label: 'Programmes', href: '#therapies-to-protocol' },
    { label: 'Platform', href: '#platform' },
    { label: 'Introduction', href: '#ageon-introduction' },
    { label: 'Achievers', href: '#achievers' },
    { label: 'Franchises', href: '#franchises' },
    { label: 'About', href: '#founder' },
];

export default function LandingHeader() {
    const [scrolled, setScrolled] = useState(false);
    const user = useAuth(s => s.user);
    const dashHref = homePathForUser(user);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 48);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    return (
        <header
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled
                    ? 'bg-white/90 backdrop-blur-xl border-b border-ink-100 shadow-[0_1px_24px_rgba(15,17,21,0.06)]'
                    : 'bg-transparent border-b border-white/10'
            }`}
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-16 lg:h-[4.5rem] flex items-center justify-between gap-4">
                <Link to="/" className="shrink-0">
                    <AgeonLogo compact variant={scrolled ? 'default' : 'white'} />
                </Link>

                <nav className="hidden lg:flex items-center gap-1" aria-label="Landing sections">
                    {NAV.map(item => (
                        <a
                            key={item.href}
                            href={item.href}
                            className={`rounded-full px-4 py-2 text-[13px] font-medium transition ${
                                scrolled
                                    ? 'text-ink-700 hover:bg-brand-teal-50 hover:text-brand-teal-700'
                                    : 'text-white/85 hover:text-white hover:bg-white/10'
                            }`}
                        >
                            {item.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-2 shrink-0">
                    <Link
                        to={dashHref}
                        className={`hidden sm:inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition ${
                            scrolled
                                ? 'bg-brand-teal text-white hover:bg-brand-teal-700'
                                : 'bg-white text-brand-teal-700 hover:bg-brand-ivory'
                        }`}
                    >
                        <LayoutDashboard className="h-3.5 w-3.5" />
                        View dashboard
                    </Link>
                    <ProfileDropdown variant="sales" onDark={!scrolled} />
                </div>
            </div>
        </header>
    );
}
